import React, { useState } from 'react';
import { css, keyframes } from '@emotion/react';
import {
  TextButton,
  Button,
  IconButton,
  IconCross,
  IconChevronDown,
  IconPlus,
} from '@flodesk/grain';

const slideIn = keyframes`
  from { transform: translateX(16px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(16px); opacity: 0; }
`;

const backdropStyles = css`
  position: fixed;
  inset: 0;
  z-index: 100;
`;

const panelStyles = css`
  position: absolute;
  top: 16px;
  right: 16px;
  bottom: 16px;
  width: 620px;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-l);
  box-shadow:
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05),
    0px 5.88px 14.05px -1.94px rgba(0, 0, 0, 0.09),
    0px 23.29px 85.96px -14px rgba(0, 0, 0, 0.19);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} var(--grn-transition-fast) ease-out;

  &[data-closing='true'] {
    animation: ${slideOut} var(--grn-transition-fast) ease-in forwards;
  }
`;

const closeButtonStyles = css`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 4;
`;

const headerStyles = css`
  padding: 40px 40px 16px;
  background: white;
  z-index: 2;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.6px;
  color: var(--grn-color-content);
`;

const bodyStyles = css`
  flex: 1;
  overflow-y: auto;
  padding: 8px 40px 32px;
  z-index: 1;
`;

const filterGroupStyles = css`
  position: relative;
  padding-top: 18px;
`;

const filterContainerStyles = css`
  border: 1px solid var(--grn-color-border);
  border-radius: var(--grn-radius-l);
  padding: 40px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const filterSectionStyles = css`
  background: var(--grn-color-background2);
  border-radius: var(--grn-radius-l);
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const filterHeaderStyles = css`
  display: flex;
  align-items: center;
  height: 36px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const chipContainerStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 460px;
`;

const chipStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 16px;
  border-radius: 32px;
  border: 1px solid var(--grn-color-fade3);
  background: var(--grn-color-background);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: pointer;
  transition:
    background var(--grn-transition-fast),
    border-color var(--grn-transition-fast);

  &:hover {
    background: var(--grn-color-background2);
  }
`;

const chipSelectedStyles = css`
  ${chipStyles};
  background: var(--grn-color-shade12);
  color: var(--grn-color-shade2);
  border-color: transparent;

  &:hover {
    background: var(--grn-color-shade12);
    opacity: 0.9;
  }
`;

const fieldStyles = css`
  display: flex;
  align-items: center;
  height: 42px;
  padding: 0 12px;
  background: white;
  border: 1px solid rgba(61, 46, 41, 0.24);
  border-radius: 6px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  cursor: pointer;
`;

const fieldChevronStyles = css`
  margin-left: auto;
  display: flex;
  align-items: center;
  color: var(--grn-color-content2);
`;

const conditionalHeaderStyles = css`
  display: flex;
  align-items: center;
  height: 36px;
  justify-content: space-between;
  width: 100%;
`;

const conditionLabelStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`;

const conditionDimStyles = css`
  color: var(--grn-color-content2);
`;

const conditionBoldStyles = css`
  color: var(--grn-color-content);
`;

const addFilterStyles = css`
  padding-top: 16px;
`;

const matchingHeaderStyles = css`
  position: absolute;
  top: 0;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  background: var(--grn-color-background);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  z-index: 1;
`;

const toggleGroupStyles = css`
  display: flex;
  height: 36px;
  background: var(--grn-color-background3);
  border-radius: var(--grn-radius-m);
  overflow: hidden;
`;

const toggleActiveStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: var(--grn-color-shade1);
  border: 3px solid var(--grn-color-background3);
  border-radius: var(--grn-radius-m);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: pointer;
`;

const toggleInactiveStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border: 3px solid transparent;
  border-radius: var(--grn-radius-m);
  background: transparent;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  cursor: pointer;
`;

const newGroupStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 40px;
  border-top: 1px solid var(--grn-color-border);
  background: white;
  z-index: 3;
`;

const footerNoteStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content2);
  width: 233px;
`;

const footerActionsStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const STATUS_OPTIONS = [
  'Active',
  'Unconfirmed',
  'Bounced',
  'Unsubscribed',
  'Marked spam',
  'Cleaned',
  'Archived',
];

type FilterModalProps = {
  initialStatuses?: string[];
  onClose: () => void;
  onApply: (statuses: string[]) => void;
};

export default function FilterModal({ initialStatuses = [], onClose, onApply }: FilterModalProps) {
  const [closing, setClosing] = useState(false);
  const [matchAll, setMatchAll] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(initialStatuses);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 200);
  };

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status],
    );
  };

  return (
    <div css={backdropStyles} role="presentation" onClick={handleClose}>
      <div
        css={panelStyles}
        data-closing={closing}
        className="grn-context"
        role="presentation"
        onClick={e => e.stopPropagation()}
      >
        <div css={closeButtonStyles}>
          <IconButton icon={<IconCross />} onClick={handleClose} />
        </div>

        <div css={headerStyles}>Filter your subscribers</div>

        <div css={bodyStyles}>
          <div css={filterGroupStyles}>
            <div css={matchingHeaderStyles}>
              <span>Matching</span>
              <div css={toggleGroupStyles}>
                <button
                  type="button"
                  css={matchAll ? toggleActiveStyles : toggleInactiveStyles}
                  onClick={() => setMatchAll(true)}
                >
                  all
                </button>
                <button
                  type="button"
                  css={!matchAll ? toggleActiveStyles : toggleInactiveStyles}
                  onClick={() => setMatchAll(false)}
                >
                  any
                </button>
              </div>
              <span>of these:</span>
            </div>

            <div css={filterContainerStyles}>
              <div css={filterSectionStyles}>
                <div css={filterHeaderStyles}>Status</div>
                <div css={chipContainerStyles}>
                  {STATUS_OPTIONS.map(status => (
                    <button
                      type="button"
                      key={status}
                      css={selectedStatuses.includes(status) ? chipSelectedStyles : chipStyles}
                      onClick={() => toggleStatus(status)}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div css={filterSectionStyles}>
                <div css={conditionalHeaderStyles}>
                  <TextButton icon={<IconChevronDown />} iconPosition="right" trimSide="left">
                    <span css={conditionLabelStyles}>
                      <span css={conditionDimStyles}>Segment</span>
                      <span css={conditionBoldStyles}> is any</span>
                    </span>
                  </TextButton>
                </div>
                <div css={fieldStyles}>
                  <span>Start typing to find a segment</span>
                  <div css={fieldChevronStyles}>
                    <IconChevronDown />
                  </div>
                </div>
              </div>

              <div css={filterSectionStyles}>
                <div css={conditionalHeaderStyles}>
                  <TextButton icon={<IconChevronDown />} iconPosition="right" trimSide="left">
                    <span css={conditionLabelStyles}>
                      <span css={conditionBoldStyles}>Source </span>
                      <span css={conditionDimStyles}>is any</span>
                    </span>
                  </TextButton>
                </div>
                <div css={fieldStyles}>
                  <span>Choose source</span>
                  <div css={fieldChevronStyles}>
                    <IconChevronDown />
                  </div>
                </div>
              </div>

              <div css={addFilterStyles}>
                <Button variant="neutral">Add filter</Button>
              </div>
            </div>
          </div>

          <div css={newGroupStyles}>
            <TextButton icon={<IconPlus />}>New filter group</TextButton>
          </div>
        </div>

        <div css={footerStyles}>
          <p css={footerNoteStyles}>
            Engagement data (like opens and clicks) can take up to 30 minutes to update
          </p>
          <div css={footerActionsStyles}>
            <TextButton onClick={() => setSelectedStatuses([])}>Clear</TextButton>
            <Button variant="accent" onClick={() => onApply(selectedStatuses)}>
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
